"use client";

import { useState, useEffect, useMemo } from "react";
import { newsService } from "@/services/newsService";
import { News, PaginatedResponse } from "@/types";
import { formatDate } from "@/utils/dateFormatter";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import NextImage from "next/image";
import NewsHeroScene from "@/components/ui/NewsHeroScene";
import { formatExcerpt } from "@/utils/htmlUtils";
import { useLanguage } from "@/context/LanguageContext";
import { Navbar, Footer } from "@/components/layout/NavbarFooter";
import { Skeleton } from "../ui/Skeleton";
import { subscriberService } from "@/services/subscriberService";
import { toast } from "react-hot-toast";

const MapSection = dynamic(() => import("@/components/ui/MapSection"), {
  ssr: false,
  loading: () => <Skeleton className="w-full h-[400px]" />,
});

const rawCategories = ["all", "tech", "event", "culture"];

const categoryMap: Record<string, string> = {
  "Công nghệ": "tech",
  "Sự kiện": "event",
  "Sự kiện nổi bật": "event",
  "Events": "event",
  "Culture": "culture",
  "Văn hóa": "culture",
  "Thể thao": "sports",
  "Teambuilding": "teambuilding",
  "Hoạt động sinh nhật": "birthday",
};

interface NewsPageContentProps {
  initialNews: News[];
  initialTotal: number;
}

export default function NewsPageContent({ initialNews, initialTotal }: NewsPageContentProps) {
  const router = useRouter();
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState<"latest" | "oldest">("latest");
  const [newsList, setNewsList] = useState<News[]>(initialNews);
  const [isLoading, setIsLoading] = useState(false); // Initial load is done on server
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(initialTotal > initialNews.length);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const LIMIT = 7;


  const fetchNews = async (pageNum: number, isLoadMore = false) => {
    try {
      if (isLoadMore) setIsLoadingMore(true);
      else setIsLoading(true);

      const response = await newsService.getNews({
        page: pageNum,
        limit: LIMIT,
        category: activeCategory !== "all" ? activeCategory : undefined,
        sortBy: "date",
        sortOrder: sortBy === "latest" ? "desc" : "asc",
      });

      let apiNews = response.docs;

      setNewsList(isLoadMore ? (prev) => [...prev, ...apiNews] : apiNews);

      const currentTotal = isLoadMore
        ? newsList.length + apiNews.length
        : apiNews.length;

      setHasMore(
        response.total
          ? currentTotal < response.total
          : apiNews.length === LIMIT,
      );
    } catch (err: any) {
      setError(err.message || "Failed to fetch news");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchNews(1);
  }, [activeCategory, sortBy]);


  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNews(nextPage, true);
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      setIsSubmitting(true);
      const response = await subscriberService.subscribe(email);
      const message = (response as any).message;

      if (message === "Bạn đã đăng ký nhận bản tin trước đó.") {
        toast.error(message);
      } else {
        toast.success(message || "Đăng ký thành công!");
        setEmail("");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || "Đăng ký thất bại. Vui lòng thử lại.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    }, observerOptions);

    const timeoutId = setTimeout(() => {
      const revealElements = document.querySelectorAll(".reveal");
      revealElements.forEach((el) => observer.observe(el));
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [isLoading, newsList]);

  const featuredNews = useMemo(() => newsList[0], [newsList]);
  const gridNews = useMemo(() => newsList.length > 1 ? newsList.slice(1) : [], [newsList]);
  
  const showNoResult = !isLoading && newsList.length === 0 && !error;

  return (
    <>
      <Navbar />
      <main className="bg-[#040d21] text-white antialiased overflow-hidden">
        <section className="relative py-40 md:py-60 overflow-hidden mb-16 min-h-[90vh] flex items-center">
          <NewsHeroScene />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="reveal reveal-up inline-flex items-center gap-3 px-5 py-2 bg-[#0a192f]/60 backdrop-blur-md rounded-full shadow-sm border border-[#00f3ff]/20 text-[#00f3ff] font-bold text-[10px] uppercase tracking-[0.2em] mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f3ff] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00f3ff] shadow-[0_0_8px_#00f3ff]"></span>
              </span>
              {t("newsPage.hero.badge")}
            </div>
            <h1 className="reveal reveal-up delay-100 text-5xl md:text-7xl font-black text-white mb-6 tracking-tighter leading-[0.95]">
              {t("newsPage.hero.title")}{" "}
              <span className="text-[#00f3ff] italic drop-shadow-[0_0_8px_rgba(0,243,255,0.4)]">
                {t("newsPage.hero.titleAccent")}
              </span>
            </h1>
            <p className="reveal reveal-up delay-200 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-light">
              {t("newsPage.hero.subtitle")}
            </p>
          </div>
        </section>

        <section className="pb-32 px-6">
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <div className="animate-fade-in">
                <div className="bg-[#0a192f]/80 backdrop-blur-xl rounded-[2.5rem] overflow-hidden border border-[#00f3ff]/10 flex flex-col lg:flex-row">
                  <Skeleton className="lg:w-1/2 min-h-[400px]" />
                  <div className="lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center space-y-6">
                    <div className="flex gap-4">
                      <Skeleton className="h-6 w-24 rounded-full" />
                      <Skeleton className="h-6 w-32 rounded-full" />
                    </div>
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-3/4" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </div>
              </div>
            ) : featuredNews ? (
              <div className="animate-fade-in">
                <div className="group relative bg-[#0a192f]/80 backdrop-blur-xl rounded-[2.5rem] overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.5)] border border-[#00f3ff]/20 flex flex-col lg:flex-row transition-all hover:border-[#00f3ff]/50 hover:shadow-[0_0_50px_rgba(0,243,255,0.1)]">
                  <div className="lg:w-1/2 overflow-hidden relative min-h-[400px]">
                    <NextImage
                      src={featuredNews.imageUrl || featuredNews.image || "/join.jpg"}
                      alt={featuredNews.title}
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                      fill
                      priority
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                    <div className="absolute top-6 left-6 z-10">
                      <div className="flex items-center gap-2 bg-[#00f3ff] text-[#040d21] px-5 py-2.5 rounded-full font-black text-xs uppercase tracking-widest shadow-[0_0_20px_#00f3ff]">
                        <span className="material-symbols-outlined text-[16px]">local_fire_department</span>
                        {t("newsPage.featured.badge")}
                      </div>
                    </div>
                  </div>
                  <div className="lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="px-4 py-1.5 bg-[#00f3ff]/10 text-[#00f3ff] border border-[#00f3ff]/20 rounded-full text-[10px] font-bold uppercase tracking-widest drop-shadow-[0_0_2px_currentColor]">
                        {t(`newsPage.filter.${categoryMap[featuredNews.category] || featuredNews.category}`)}
                      </span>
                      <span className="text-slate-400 text-xs font-bold tracking-widest uppercase mb-1">
                        {formatDate(featuredNews.date)}
                      </span>
                    </div>
                    <h2
                      className="text-3xl lg:text-4xl font-black mb-6 leading-tight text-white group-hover:text-[#00f3ff] transition-colors drop-shadow-md"
                      dangerouslySetInnerHTML={{ 
                        __html: (t(`newsPage.newsItems.${featuredNews.slug}`) as any)?.title || featuredNews.title 
                      }}
                    />
                    <p className="text-slate-300 text-lg mb-8 leading-relaxed font-light line-clamp-3">
                      {(t(`newsPage.newsItems.${featuredNews.slug}`) as any)?.excerpt || formatExcerpt(featuredNews.excerpt || featuredNews.content, 150)}
                    </p>
                    <button
                      onClick={() => router.push(`/news/${featuredNews.slug}`)}
                      className="flex items-center gap-3 text-[#00f3ff] font-black group-hover:gap-5 transition-all text-left cursor-pointer uppercase tracking-widest text-sm"
                    >
                      {t("newsPage.featured.readArticle")}
                      <span className="material-symbols-outlined text-[#00f3ff] drop-shadow-[0_0_5px_currentColor]">arrow_forward</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="py-24 bg-[#0a192f]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-10 mb-20">
              <div className="flex bg-[#040d21]/80 backdrop-blur-md p-2 rounded-2xl shadow-xl border border-[#00f3ff]/20 overflow-x-auto max-w-full">
                {rawCategories.map((catKey) => (
                  <button
                    key={catKey}
                    onClick={() => setActiveCategory(catKey)}
                    className={`px-8 py-3.5 rounded-xl text-sm font-black whitespace-nowrap transition-all uppercase tracking-widest cursor-pointer ${
                      activeCategory === catKey
                        ? "bg-[#00f3ff] text-[#040d21] shadow-[0_0_20px_#00f3ff]"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {t(`newsPage.filter.${catKey}`)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-4 text-slate-400 text-sm font-bold tracking-widest uppercase">
                {t("newsPage.filter.sortBy")}
                <div className="flex bg-[#040d21]/60 backdrop-blur-md p-1 rounded-xl border border-[#00f3ff]/20">
                  <button
                    onClick={() => setSortBy("latest")}
                    className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${
                      sortBy === "latest" ? "bg-[#00f3ff] text-[#040d21]" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {t("newsPage.filter.latest")}
                  </button>
                  <button
                    onClick={() => setSortBy("oldest")}
                    className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${
                      sortBy === "oldest" ? "bg-[#00f3ff] text-[#040d21]" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {t("newsPage.filter.oldest")}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="text-center py-10 bg-red-500/10 border border-red-500/20 rounded-2xl mb-12">
                <p className="text-red-400 font-bold">{error}</p>
                <button onClick={() => window.location.reload()} className="mt-4 text-[#00f3ff] font-bold underline">
                  Thử lại
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
              {gridNews.map((item: News) => (
                <div
                  key={item.id}
                  className={`animate-fade-in group bg-[#040d21]/60 backdrop-blur-xl rounded-[2rem] overflow-hidden shadow-xl hover:shadow-[0_0_30px_rgba(0,243,255,0.15)] transition-all duration-500 border border-[#00f3ff]/20 hover:border-[#00f3ff]/50 hover:-translate-y-2 cursor-pointer`}
                  onClick={() => router.push(`/news/${item.slug}`)}
                >
                  <div className="relative h-64 overflow-hidden">
                    <NextImage
                      src={item.imageUrl || item.image || "/join.jpg"}
                      alt={item.title}
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-1.5 bg-[#040d21]/80 backdrop-blur-md text-[#00f3ff] border border-[#00f3ff]/30 rounded-lg text-[10px] font-black uppercase tracking-[0.2em] shadow-lg drop-shadow-[0_0_2px_currentColor]">
                        {t(`newsPage.filter.${categoryMap[item.category] || item.category}`)}
                      </span>
                    </div>
                  </div>
                  <div className="p-8">
                    <div className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm text-[#00f3ff]">calendar_today</span>
                      {formatDate(item.date)}
                    </div>
                    <h3
                      className="text-xl font-black mb-4 leading-tight text-white group-hover:text-[#00f3ff] transition-colors line-clamp-2 cursor-pointer drop-shadow-sm"
                      dangerouslySetInnerHTML={{ 
                        __html: (t(`newsPage.newsItems.${item.slug}`) as any)?.title || item.title 
                      }}
                    />
                    <p className="text-slate-400 text-sm leading-relaxed mb-8 line-clamp-3 font-medium">
                      {(t(`newsPage.newsItems.${item.slug}`) as any)?.excerpt || formatExcerpt(item.excerpt || item.content, 150)}
                    </p>
                    <button
                      onClick={() => router.push(`/news/${item.slug}`)}
                      className="flex items-center gap-2 text-[#00f3ff] font-black text-[10px] uppercase tracking-[0.2em] group-hover:gap-4 transition-all cursor-pointer"
                    >
                      {t("newsPage.grid.readMore")}
                      <span className="material-symbols-outlined text-sm">arrow_right_alt</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {showNoResult && (
              <div className="text-center py-20 text-slate-500 italic">
                {t("newsPage.grid.noResult") || "Không tìm thấy bài viết nào."}
              </div>
            )}

            {hasMore && (
              <div className="text-center mt-12 mb-24">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="px-12 py-5 rounded-2xl border-2 border-[#00f3ff]/30 text-[#00f3ff] font-black uppercase tracking-widest text-xs hover:bg-[#00f3ff] hover:text-[#040d21] hover:shadow-[0_0_30px_#00f3ff] transition-all cursor-pointer bg-transparent disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px] flex items-center justify-center mx-auto gap-3"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#00f3ff]/30 border-t-[#00f3ff] rounded-full animate-spin"></div>
                      {t("newsPage.grid.loading")}
                    </>
                  ) : (
                    t("newsPage.grid.loadMore")
                  )}
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="py-32 px-6 bg-[#040d21]">
          <div className="max-w-5xl mx-auto bg-[#0a192f]/60 backdrop-blur-xl p-12 lg:p-20 rounded-[3rem] relative overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.8)] border border-[#00f3ff]/20 group">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#00f3ff]/5 rounded-full blur-[100px] -z-10 animate-pulse-slow"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#00f3ff]/10 rounded-full blur-[120px] -z-10"></div>

            <div className="relative z-10 text-center max-w-2xl mx-auto reveal reveal-up">
              <h2 className="text-4xl lg:text-5xl font-black mb-6 tracking-tighter text-white leading-tight">
                {t("newsPage.newsletter.title")}{" "}
                <span className="text-[#00f3ff] drop-shadow-[0_0_8px_rgba(0,243,255,0.4)]">
                  {t("newsPage.newsletter.titleAccent")}
                </span>
              </h2>
              <p className="text-slate-300 text-lg mb-12 font-light leading-relaxed">
                {t("newsPage.newsletter.subtitle")}
              </p>
              <form className="flex flex-col sm:flex-row gap-4" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("newsPage.newsletter.placeholder")}
                  disabled={isSubmitting}
                  className="flex-1 px-8 py-5 rounded-2xl bg-[#040d21]/80 border border-[#00f3ff]/20 focus:border-[#00f3ff]/50 focus:ring-1 focus:ring-[#00f3ff]/50 outline-none text-white font-medium transition-all"
                  required
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#00f3ff] text-[#040d21] px-10 py-5 rounded-2xl font-black uppercase tracking-widest text-sm hover:shadow-[0_0_30px_#00f3ff] transition-all active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 lg:min-w-[180px]"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#040d21]/30 border-t-[#040d21] rounded-full animate-spin"></div>
                      {t("newsPage.grid.loading")}
                    </>
                  ) : (
                    t("newsPage.newsletter.cta")
                  )}
                </button>
              </form>
              <p className="text-slate-400 text-[10px] mt-8 font-bold tracking-wider uppercase opacity-80">
                {t("newsPage.newsletter.privacy")}
              </p>
            </div>
          </div>
        </section>
        <MapSection />
      </main>
      <Footer />
    </>
  );
}
